/**
 * Screen Wake Lock.
 *
 * A performance surface that dims after thirty seconds is unusable: the player is holding
 * pads, not touching the screen in the way iOS counts, and the display sleeps mid-take. So
 * PadLab holds a wake lock while the transport is running, a recording is armed, or a pad is
 * looping — and gives it straight back when none of those is true, because holding the screen
 * awake through an idle project is the single rudest thing an app can do to a battery.
 *
 * The lock is reference counted. The transport, the recorder and the sequencer each ask
 * independently and each get a release function; the hardware lock exists exactly while at
 * least one of them still wants it.
 *
 * Where the API does not exist the module is a silent no-op — never an error, never a
 * message. The honest report lives in the capability matrix and in `wakeLockDiagnostics()`,
 * which is what the settings screen shows.
 */

import { computed, signal, type ReadonlySignal } from '../core/signal';
import { createLogger, shape, tag } from '../core/log';

const log = createLogger('wake-lock');

// Declared locally and reached through an `unknown` cast so this file compiles the same way
// whether or not the installed TypeScript lib happens to describe Wake Lock yet.
interface WakeLockSentinelLike extends EventTarget {
  readonly released: boolean;
  release(): Promise<void>;
}

interface WakeLockLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

const api: WakeLockLike | undefined = (navigator as unknown as { wakeLock?: WakeLockLike })
  .wakeLock;

/** Whether this browser implements Screen Wake Lock at all. */
export const screenWakeLockSupported = typeof api?.request === 'function';

const activeSignal = signal(false);
const holdersSignal = signal(0);

/** True while the display is genuinely being held awake by us. */
export const wakeLockActive: ReadonlySignal<boolean> = activeSignal;

/** True while something in the app still wants the screen awake, held or not. */
export const wakeLockWanted: ReadonlySignal<boolean> = computed(() => holdersSignal.value > 0);

let sentinel: WakeLockSentinelLike | null = null;
let inFlight = false;
/** The reason the last acquisition failed, for the settings screen. Never shown as a toast. */
let lastFailure: string | null = null;

function onSentinelRelease(): void {
  // Fired by the system, almost always because the page became hidden. Re-acquisition is
  // handled by the visibilitychange listener; requesting again from here would be rejected.
  sentinel = null;
  activeSignal.value = false;
}

async function ensureLock(): Promise<void> {
  if (api === undefined) return;
  if (holdersSignal.peek() === 0) return;
  if (sentinel !== null && !sentinel.released) return;
  // `request()` rejects outright on a hidden document, so there is no point in asking.
  if (document.visibilityState !== 'visible') return;
  if (inFlight) return;

  inFlight = true;
  try {
    const next = await api.request('screen');
    if (holdersSignal.peek() === 0) {
      // Everything that wanted the lock let go while we were awaiting it.
      await next.release().catch(() => undefined);
      return;
    }
    next.addEventListener('release', onSentinelRelease, { once: true });
    sentinel = next;
    activeSignal.value = true;
    lastFailure = null;
  } catch (error) {
    // NotAllowedError is the normal answer in Low Power Mode or on a hidden page. It is not
    // a failure the user needs to see: the screen simply dims on the system timer.
    activeSignal.value = false;
    lastFailure = error instanceof Error ? `${error.name}: ${error.message}` : 'request() failed';
    log.debug('Screen wake lock refused', { cause: shape(error) });
  } finally {
    inFlight = false;
  }
}

async function dropLock(): Promise<void> {
  const current = sentinel;
  sentinel = null;
  activeSignal.value = false;
  if (current === null) return;
  current.removeEventListener('release', onSentinelRelease);
  await current.release().catch(() => undefined);
}

/**
 * Asks for the screen to stay awake and returns the function that gives it back. Calling the
 * returned function more than once is harmless.
 *
 * `reason` is a developer-authored label used only for diagnostics — never user content.
 */
export function holdScreenAwake(reason: string): () => void {
  holdersSignal.value = holdersSignal.peek() + 1;
  log.debug('Wake lock requested', { reason: tag(reason), holders: holdersSignal.peek() });
  void ensureLock();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    holdersSignal.value = Math.max(0, holdersSignal.peek() - 1);
    if (holdersSignal.peek() === 0) void dropLock();
  };
}

/**
 * Drops every outstanding hold. For the panic/stop-all control and for teardown — after this
 * the next `holdScreenAwake` starts from zero.
 */
export function releaseAllScreenHolds(): void {
  holdersSignal.value = 0;
  void dropLock();
}

export interface WakeLockDiagnostics {
  readonly supported: boolean;
  readonly active: boolean;
  readonly holders: number;
  /** One sentence describing the live state, suitable for the settings screen. */
  readonly detail: string;
}

export function wakeLockDiagnostics(): WakeLockDiagnostics {
  const holders = holdersSignal.peek();
  const active = activeSignal.peek();

  let detail: string;
  if (!screenWakeLockSupported) {
    detail =
      'This browser does not implement Screen Wake Lock. The display sleeps on the system timer during a performance; playback and recording continue.';
  } else if (active) {
    detail = `Held. The display will not sleep while ${holders === 1 ? 'one part' : `${holders} parts`} of PadLab is performing.`;
  } else if (holders > 0) {
    detail = lastFailure === null
      ? 'Requested, waiting for the app to come back to the foreground.'
      : `Requested but refused (${lastFailure}). Low Power Mode blocks wake locks.`;
  } else {
    detail = 'Not needed right now. PadLab takes the lock only while the transport is running.';
  }

  return { supported: screenWakeLockSupported, active, holders, detail };
}

/**
 * The system drops the lock whenever the page is hidden — a call, a swipe to another app, a
 * screen lock — and does not give it back. Without this listener the screen would keep the
 * lock for exactly one foreground session and then quietly stop working for the rest of the
 * project, which is the failure mode this API is famous for.
 */
if (screenWakeLockSupported) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void ensureLock();
  });
  // Returning from the bfcache does not always fire visibilitychange on iOS.
  window.addEventListener('pageshow', () => {
    if (document.visibilityState === 'visible') void ensureLock();
  });
}
